import React, { useEffect, useState } from 'react'
import { WizardState, SubjectWeightage, ChapterWeightage, WeightageMode } from '../Index'
import { buildDefaultTestTitle } from '../titleHelper'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText, Clock, BarChart3, CheckCircle2, AlertTriangle, AlertCircle,
  ChevronDown, ChevronRight, Equal, Percent, Hash, Sliders, RefreshCw,
  Zap, Sparkles, Scale, Layers, Database
} from 'lucide-react'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
}

// Helper: Check if a subject belongs to a blueprint section
function isSubjectMatchingSection(subId: number, subName: string, sec: any): boolean {
  if (!sec) return false

  const secSubIds = Array.isArray(sec.subject_ids)
    ? sec.subject_ids.map(Number)
    : (sec.subject_id ? [Number(sec.subject_id)] : [])

  if (secSubIds.length > 0) {
    return secSubIds.includes(Number(subId))
  }

  const sName = (subName || '').toLowerCase().trim()

  if (sec.subject_hint) {
    const hint = String(sec.subject_hint).toLowerCase().trim()
    if (sName.includes(hint) || hint.includes(sName)) return true
  }

  const secName = (sec.name || '').toLowerCase().trim()
  if (secName) {
    return sName.includes(secName) || secName.includes(sName)
  }

  return false
}

// Helper: Distribute questions across chapters proportionally up to available pool
function distributeQuestionsToChapters(
  chapters: { id?: number; chapter_id?: number; name: string; available: number }[] | ChapterWeightage[],
  targetQ: number
): ChapterWeightage[] {
  if (!chapters || chapters.length === 0) return []
  if (targetQ <= 0) {
    return chapters.map(ch => ({
      chapter_id: ('chapter_id' in ch && ch.chapter_id) ? ch.chapter_id : ('id' in ch ? (ch.id as number) : 0),
      name: ch.name,
      percentage: 0,
      calculated_questions: 0,
      available: ch.available || 0
    }))
  }

  const totalAvail = chapters.reduce((sum, ch) => sum + (ch.available || 0), 0)
  let remainingTarget = targetQ
  let remainingAvail = totalAvail

  const result: ChapterWeightage[] = chapters.map((ch, idx) => {
    const chAvail = ch.available || 0
    let chIdeal = 0
    if (idx === chapters.length - 1) {
      chIdeal = Math.min(chAvail, Math.max(0, remainingTarget))
    } else if (remainingAvail > 0) {
      chIdeal = Math.round((chAvail / remainingAvail) * remainingTarget)
      chIdeal = Math.min(chAvail, Math.max(0, chIdeal))
    }
    remainingTarget -= chIdeal
    remainingAvail -= chAvail
    return {
      chapter_id: ('chapter_id' in ch && ch.chapter_id) ? ch.chapter_id : ('id' in ch ? (ch.id as number) : 0),
      name: ch.name,
      percentage: 0,
      calculated_questions: chIdeal,
      available: chAvail
    }
  })

  // If there is still unallocated remainingTarget due to individual chapter caps
  let unallocated = targetQ - result.reduce((sum, ch) => sum + ch.calculated_questions, 0)
  if (unallocated > 0) {
    for (const ch of result) {
      if (ch.calculated_questions < ch.available) {
        const add = Math.min(unallocated, ch.available - ch.calculated_questions)
        ch.calculated_questions += add
        unallocated -= add
        if (unallocated === 0) break
      }
    }
  }

  // Update percentages
  result.forEach(ch => {
    ch.percentage = targetQ > 0 ? Math.round((ch.calculated_questions / targetQ) * 100) : 0
  })

  return result
}

export default function StepWeightage({ state, updateState }: Props) {
  const [expandedSubjects, setExpandedSubjects] = useState<Record<number, boolean>>({})
  const isPractice = state.testType === 'practice'

  // Initialize test title if blank
  useEffect(() => {
    if (!state.title && state.syllabiData.length > 0) {
      const firstSyl = state.syllabiData[0]
      const cleanTitle = buildDefaultTestTitle({
        examName: firstSyl?.name,
        testTypeKey: state.testType,
        syllabusName: firstSyl?.name
      })
      updateState({ title: cleanTitle })
    }
  }, [state.syllabiData, state.testType])

  // Extract subjects & chapters from poolBreakdown or selectedSyllabi
  useEffect(() => {
    const pool = state.poolBreakdown

    // If subjectWeightages already exists, check if any subject is stale (has 0 available in pool)
    const isStale = pool && state.subjectWeightages.length > 0 && state.subjectWeightages.some(sw => {
      const avail = pool.by_subject?.[sw.subject_id]?.count ?? pool.by_subject?.[String(sw.subject_id)]?.count ?? 0
      return avail === 0
    })

    if (state.subjectWeightages.length > 0 && !isStale) return

    const selectedSyllabi = state.syllabiData.filter(s => state.selectedSyllabiIds.includes(s.id))

    // Collect distinct subjects that have available questions in the pool
    const subjectsMap = new Map<number, { id: number; name: string; available: number; chapters: { id: number; name: string; available: number }[] }>()

    selectedSyllabi.forEach(syl => {
      syl.subjects.forEach(sub => {
        const subId = sub.subject_id || sub.id
        if (!subId) return
        if (state.filterSubjectIds.length > 0 && !state.filterSubjectIds.includes(subId)) return

        const poolSubCount = pool?.by_subject?.[subId]?.count ?? pool?.by_subject?.[String(subId)]?.count ?? sub.questions_count ?? 0
        // If pool is loaded, exclude subjects with 0 matching questions
        if (pool && poolSubCount === 0) return

        if (!subjectsMap.has(subId)) {
          subjectsMap.set(subId, {
            id: subId,
            name: sub.name,
            available: poolSubCount,
            chapters: []
          })
        }

        const currentSub = subjectsMap.get(subId)!

        sub.chapters.forEach(chap => {
          const chapId = chap.chapter_id || chap.id
          if (!chapId) return
          if (state.filterChapterIds.length > 0 && !state.filterChapterIds.includes(chapId)) return

          const poolChapCount = pool?.by_chapter?.[chapId]?.count ?? pool?.by_chapter?.[String(chapId)]?.count ?? chap.questions_count ?? 0
          if (pool && poolChapCount === 0) return

          if (!currentSub.chapters.some(c => c.id === chapId)) {
            currentSub.chapters.push({
              id: chapId,
              name: chap.name,
              available: poolChapCount
            })
          }
        })
      })
    })

    let subjectsList = Array.from(subjectsMap.values())

    // Fallback: if subjectsMap is empty but pool has by_subject entries
    if (subjectsList.length === 0 && pool?.by_subject) {
      Object.entries(pool.by_subject).forEach(([sidStr, sbData]) => {
        const sid = Number(sidStr)
        const cnt = sbData.count || 0
        if (cnt > 0) {
          subjectsList.push({
            id: sid,
            name: sbData.name,
            available: cnt,
            chapters: []
          })
        }
      })
    }

    if (subjectsList.length === 0) return

    const marksPerQ = state.markingScheme.positive_marks || 1
    const activeSecs = (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)

    let initialSubjectWeightages: SubjectWeightage[] = []
    let targetTotalQ = state.totalQuestions

    if (activeSecs.length > 0) {
      // 🎯 Blueprint Mode: Allocate strictly to section targets
      const sectionTargetSum = activeSecs.reduce((sum: number, s: any) => sum + (Number(s.total_questions) || 0), 0)
      if (sectionTargetSum > 0) {
        targetTotalQ = sectionTargetSum
      }

      const subjectAllocationMap = new Map<number, number>()
      const allocatedSubjectIds = new Set<number>()

      activeSecs.forEach((sec: any) => {
        const matchingSubs = subjectsList.filter(sub => isSubjectMatchingSection(sub.id, sub.name, sec))
        if (matchingSubs.length === 0) return

        const secTarget = Number(sec.total_questions) || 0
        const totalMatchingAvail = matchingSubs.reduce((sum, s) => sum + (s.available || 0), 0)

        let remSecTarget = secTarget
        let remSecAvail = totalMatchingAvail

        matchingSubs.forEach((sub, loopIdx) => {
          allocatedSubjectIds.add(sub.id)
          let ideal = 0
          if (loopIdx === matchingSubs.length - 1) {
            ideal = Math.min(sub.available, Math.max(0, remSecTarget))
          } else if (remSecAvail > 0) {
            ideal = Math.round((sub.available / remSecAvail) * remSecTarget)
            ideal = Math.min(sub.available, Math.max(0, ideal))
          }
          remSecTarget -= ideal
          remSecAvail -= sub.available
          subjectAllocationMap.set(sub.id, (subjectAllocationMap.get(sub.id) || 0) + ideal)
        })
      })

      // Any subjects that didn't match a section get 0 initial questions
      subjectsList.forEach(sub => {
        if (!allocatedSubjectIds.has(sub.id)) {
          subjectAllocationMap.set(sub.id, 0)
        }
      })

      initialSubjectWeightages = subjectsList.map(sub => {
        const qCount = subjectAllocationMap.get(sub.id) || 0
        const pct = targetTotalQ > 0 ? Math.round((qCount / targetTotalQ) * 100) : 0
        const marksVal = qCount * marksPerQ
        const chapters = distributeQuestionsToChapters(sub.chapters, qCount)

        return {
          subject_id: sub.id,
          name: sub.name,
          percentage: pct,
          marks: marksVal,
          calculated_questions: qCount,
          available: sub.available,
          chapters: chapters
        }
      })
    } else {
      // Flat Mode (no sections): Distribute targetTotalQ across subjects
      if (targetTotalQ <= 0 || (pool && targetTotalQ > pool.total)) {
        targetTotalQ = pool ? Math.min(pool.total, Math.max(1, state.totalQuestions || 35)) : 35
      }

      const totalAvail = subjectsList.reduce((sum, s) => sum + (s.available || 0), 0)
      let remTarget = targetTotalQ
      let remAvail = totalAvail

      initialSubjectWeightages = subjectsList.map((sub, idx) => {
        let qCount = 0
        if (idx === subjectsList.length - 1) {
          qCount = Math.min(sub.available, Math.max(0, remTarget))
        } else if (remAvail > 0) {
          qCount = Math.round((sub.available / remAvail) * remTarget)
          qCount = Math.min(sub.available, Math.max(0, qCount))
        }
        remTarget -= qCount
        remAvail -= sub.available

        const pct = targetTotalQ > 0 ? Math.round((qCount / targetTotalQ) * 100) : 0
        const marksVal = qCount * marksPerQ
        const chapters = distributeQuestionsToChapters(sub.chapters, qCount)

        return {
          subject_id: sub.id,
          name: sub.name,
          percentage: pct,
          marks: marksVal,
          calculated_questions: qCount,
          available: sub.available,
          chapters: chapters
        }
      })
    }

    updateState({
      totalQuestions: targetTotalQ,
      totalMarks: targetTotalQ * marksPerQ,
      subjectWeightages: initialSubjectWeightages,
      weightageMode: 'questions'
    })
  }, [state.poolBreakdown, state.syllabiData, state.selectedSyllabiIds, state.filterSubjectIds, state.filterChapterIds, state.blueprintSections])

  // Recalculate question counts & marks whenever totalQuestions, totalMarks, or weightages change
  const recalculateWeightages = (
    weightages: SubjectWeightage[],
    totalQ: number,
    totalM: number,
    mode: WeightageMode
  ): SubjectWeightage[] => {
    const marksPerQ = state.markingScheme.positive_marks || 1

    return weightages.map(sw => {
      let qCount = 0
      let marksVal = 0
      let pctVal = sw.percentage ?? 0

      if (mode === 'percentage') {
        qCount = Math.round((pctVal / 100) * totalQ)
        marksVal = Math.round((pctVal / 100) * totalM)
      } else if (mode === 'questions') {
        qCount = sw.calculated_questions ?? 0
        pctVal = totalQ > 0 ? Math.round((qCount / totalQ) * 100) : 0
        marksVal = qCount * marksPerQ
      } else {
        marksVal = sw.marks ?? 0
        qCount = marksPerQ > 0 ? Math.round(marksVal / marksPerQ) : 0
        pctVal = totalM > 0 ? Math.round((marksVal / totalM) * 100) : 0
      }

      // Update chapters proportionally
      const updatedChapters = distributeQuestionsToChapters(sw.chapters, qCount)

      return {
        ...sw,
        percentage: pctVal,
        calculated_questions: qCount,
        marks: marksVal,
        chapters: updatedChapters
      }
    })
  }

  // Helper to parse input values properly without sticking leading zeros
  const parseNum = (val: string): number => {
    if (!val || val.trim() === '') return 0
    const parsed = Number(val)
    return isNaN(parsed) ? 0 : parsed
  }

  const handleTotalQuestionsChange = (valStr: string) => {
    const totalQ = Math.max(0, parseNum(valStr))
    const marksPerQ = state.markingScheme.positive_marks || 1
    const totalM = totalQ * marksPerQ

    // First adjust base marks and questions by weightage mode
    let recalculated = recalculateWeightages(state.subjectWeightages, totalQ, totalM, state.weightageMode)

    // If totalQ > 0 and we have subjects, auto-balance subject question counts proportionally to match totalQ exactly
    if (totalQ > 0 && recalculated.length > 0) {
      const currentSum = recalculated.reduce((sum, sw) => sum + sw.calculated_questions, 0)
      if (currentSum !== totalQ) {
        let allocated = 0
        recalculated = recalculated.map((sw, idx) => {
          if (idx === recalculated.length - 1) {
            const finalQ = Math.max(0, totalQ - allocated)
            return {
              ...sw,
              calculated_questions: finalQ,
              percentage: totalQ > 0 ? Math.round((finalQ / totalQ) * 100) : 0,
              marks: finalQ * marksPerQ,
              chapters: distributeQuestionsToChapters(sw.chapters, finalQ)
            }
          }
          const propQ = currentSum > 0 ? Math.round((sw.calculated_questions / currentSum) * totalQ) : Math.floor(totalQ / recalculated.length)
          allocated += propQ
          return {
            ...sw,
            calculated_questions: propQ,
            percentage: totalQ > 0 ? Math.round((propQ / totalQ) * 100) : 0,
            marks: propQ * marksPerQ,
            chapters: distributeQuestionsToChapters(sw.chapters, propQ)
          }
        })
      }
    }

    updateState({ totalQuestions: totalQ, totalMarks: totalM, subjectWeightages: recalculated })
  }

  const handleWeightageModeChange = (mode: WeightageMode) => {
    const recalculated = recalculateWeightages(state.subjectWeightages, state.totalQuestions, state.totalMarks, mode)
    updateState({ weightageMode: mode, subjectWeightages: recalculated })
  }

  const handleSubjectPercentageChange = (subjectId: number, valStr: string) => {
    const pct = parseNum(valStr)
    const updated = state.subjectWeightages.map(sw => {
      if (sw.subject_id !== subjectId) return sw
      return { ...sw, percentage: pct }
    })
    const recalculated = recalculateWeightages(updated, state.totalQuestions, state.totalMarks, 'percentage')
    updateState({ subjectWeightages: recalculated })
  }

  const handleSubjectQuestionsChange = (subjectId: number, valStr: string) => {
    const qCount = parseNum(valStr)
    const marksPerQ = state.markingScheme.positive_marks || 1
    const updated = state.subjectWeightages.map(sw => {
      if (sw.subject_id !== subjectId) return sw
      const pct = state.totalQuestions > 0 ? Math.round((qCount / state.totalQuestions) * 100) : 0
      const marksVal = qCount * marksPerQ
      const updatedChaps = distributeQuestionsToChapters(sw.chapters, qCount)
      return {
        ...sw,
        calculated_questions: qCount,
        percentage: pct,
        marks: marksVal,
        chapters: updatedChaps
      }
    })
    updateState({ subjectWeightages: updated })
  }

  const handleSubjectMarksChange = (subjectId: number, valStr: string) => {
    const marks = parseNum(valStr)
    const updated = state.subjectWeightages.map(sw => {
      if (sw.subject_id !== subjectId) return sw
      return { ...sw, marks: marks }
    })
    const recalculated = recalculateWeightages(updated, state.totalQuestions, state.totalMarks, 'marks')
    updateState({ subjectWeightages: recalculated })
  }

  const handleChapterPercentageChange = (subjectId: number, chapterId: number, valStr: string) => {
    const pct = parseNum(valStr)
    const updated = state.subjectWeightages.map(sw => {
      if (sw.subject_id !== subjectId) return sw
      const updatedChaps = sw.chapters.map(ch => {
        if (ch.chapter_id !== chapterId) return ch
        return {
          ...ch,
          percentage: pct,
          calculated_questions: Math.round((pct / 100) * sw.calculated_questions)
        }
      })
      return { ...sw, chapters: updatedChaps }
    })
    updateState({ subjectWeightages: updated })
  }

  const handleChapterQuestionsChange = (subjectId: number, chapterId: number, valStr: string) => {
    const qCount = parseNum(valStr)
    const updated = state.subjectWeightages.map(sw => {
      if (sw.subject_id !== subjectId) return sw
      const updatedChaps = sw.chapters.map(ch => {
        if (ch.chapter_id !== chapterId) return ch
        const pct = sw.calculated_questions > 0 ? Math.round((qCount / sw.calculated_questions) * 100) : 0
        return {
          ...ch,
          calculated_questions: qCount,
          percentage: pct
        }
      })
      return { ...sw, chapters: updatedChaps }
    })
    updateState({ subjectWeightages: updated })
  }

  // ⚡ Intelligent Auto-Balance from Available Question Pool
  const handleAutoBalanceFromPool = () => {
    const marksPerQ = state.markingScheme.positive_marks || 1
    const activeSecs = (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)

    if (activeSecs.length > 0) {
      handleFillStrictlySectionTargets()
      return
    }

    const targetTotal = state.totalQuestions
    if (targetTotal <= 0 || state.subjectWeightages.length === 0) return

    const totalAvailable = state.subjectWeightages.reduce((sum, s) => sum + (s.available || 0), 0)
    if (totalAvailable === 0) return

    let remainingTarget = targetTotal
    let remainingAvailable = totalAvailable

    const updatedSubjects = state.subjectWeightages.map((sw, idx) => {
      if (sw.available <= 0 || remainingAvailable <= 0) {
        return {
          ...sw,
          calculated_questions: 0,
          percentage: 0,
          marks: 0,
          chapters: distributeQuestionsToChapters(sw.chapters, 0)
        }
      }

      let ideal = 0
      if (idx === state.subjectWeightages.length - 1) {
        ideal = Math.min(sw.available, Math.max(0, remainingTarget))
      } else {
        ideal = Math.round((sw.available / remainingAvailable) * remainingTarget)
        ideal = Math.min(sw.available, Math.max(0, ideal))
      }

      remainingTarget -= ideal
      remainingAvailable -= sw.available

      return {
        ...sw,
        calculated_questions: ideal,
        percentage: targetTotal > 0 ? Math.round((ideal / targetTotal) * 100) : 0,
        marks: ideal * marksPerQ,
        chapters: distributeQuestionsToChapters(sw.chapters, ideal)
      }
    })

    // If there's still unallocated questions due to pool caps, distribute to any subjects with remaining capacity
    let currentSum = updatedSubjects.reduce((sum, s) => sum + s.calculated_questions, 0)
    let diff = targetTotal - currentSum
    if (diff > 0) {
      for (const sw of updatedSubjects) {
        if (sw.calculated_questions < sw.available) {
          const add = Math.min(diff, sw.available - sw.calculated_questions)
          sw.calculated_questions += add
          sw.marks = sw.calculated_questions * marksPerQ
          sw.percentage = Math.round((sw.calculated_questions / targetTotal) * 100)
          sw.chapters = distributeQuestionsToChapters(sw.chapters, sw.calculated_questions)
          diff -= add
          if (diff === 0) break
        }
      }
    }

    updateState({
      subjectWeightages: updatedSubjects,
      weightageMode: 'questions'
    })
  }

  // Strictly fill questions per Section Blueprint targets (if pool is short, fills maximum available)
  const handleFillStrictlySectionTargets = () => {
    const marksPerQ = state.markingScheme.positive_marks || 1
    const activeSecs = (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)
    if (activeSecs.length === 0) {
      handleAutoBalanceFromPool()
      return
    }

    let updatedSubjects = [...state.subjectWeightages]
    const sectionTargetSum = activeSecs.reduce((sum: number, s: any) => sum + (Number(s.total_questions) || 0), 0)
    const shouldScale = state.totalQuestions > 0 && sectionTargetSum > 0 && state.totalQuestions !== sectionTargetSum
    let remScaledTotal = state.totalQuestions

    activeSecs.forEach((sec: any, sIdx: number) => {
      const sectionSubjectIndices = updatedSubjects
        .map((sw, idx) => ({ sw, idx }))
        .filter(({ sw }) => isSubjectMatchingSection(sw.subject_id, sw.name, sec))

      if (sectionSubjectIndices.length === 0) return

      let secTarget = Number(sec.total_questions) || 0
      if (shouldScale) {
        if (sIdx === activeSecs.length - 1) {
          secTarget = Math.max(0, remScaledTotal)
        } else {
          secTarget = Math.round((secTarget / sectionTargetSum) * state.totalQuestions)
          remScaledTotal -= secTarget
        }
      }

      const secAvail = sectionSubjectIndices.reduce((sum, { sw }) => sum + (sw.available || 0), 0)

      let remSecTarget = secTarget
      let remSecAvail = secAvail

      sectionSubjectIndices.forEach(({ sw, idx }, loopIdx) => {
        let ideal = 0
        if (loopIdx === sectionSubjectIndices.length - 1) {
          ideal = Math.min(sw.available, Math.max(0, remSecTarget))
        } else if (remSecAvail > 0) {
          ideal = Math.round((sw.available / remSecAvail) * remSecTarget)
          ideal = Math.min(sw.available, Math.max(0, ideal))
        }

        remSecTarget -= ideal
        remSecAvail -= sw.available

        const updatedChaps = distributeQuestionsToChapters(sw.chapters, ideal)

        updatedSubjects[idx] = {
          ...sw,
          calculated_questions: ideal,
          percentage: state.totalQuestions > 0 ? Math.round((ideal / state.totalQuestions) * 100) : 0,
          marks: ideal * marksPerQ,
          chapters: updatedChaps
        }
      })
    })

    const finalTotalQ = sectionTargetSum > 0 && !shouldScale ? sectionTargetSum : state.totalQuestions

    updateState({
      totalQuestions: finalTotalQ,
      totalMarks: finalTotalQ * marksPerQ,
      subjectWeightages: updatedSubjects,
      weightageMode: 'questions'
    })
  }

  const handleAutoBalanceSection = (sec: any) => {
    const secTarget = Number(sec.total_questions) || 0
    const marksPerQ = state.markingScheme.positive_marks || 1
    const updatedSubjects = [...state.subjectWeightages]

    const sectionSubjectIndices = updatedSubjects
      .map((sw, idx) => ({ sw, idx }))
      .filter(({ sw }) => isSubjectMatchingSection(sw.subject_id, sw.name, sec))

    if (sectionSubjectIndices.length === 0) return

    const secAvail = sectionSubjectIndices.reduce((sum, { sw }) => sum + (sw.available || 0), 0)
    let remSecTarget = secTarget
    let remSecAvail = secAvail

    sectionSubjectIndices.forEach(({ sw, idx }, loopIdx) => {
      let ideal = 0
      if (loopIdx === sectionSubjectIndices.length - 1) {
        ideal = Math.min(sw.available, Math.max(0, remSecTarget))
      } else if (remSecAvail > 0) {
        ideal = Math.round((sw.available / remSecAvail) * remSecTarget)
        ideal = Math.min(sw.available, Math.max(0, ideal))
      }

      remSecTarget -= ideal
      remSecAvail -= sw.available

      const updatedChaps = distributeQuestionsToChapters(sw.chapters, ideal)

      updatedSubjects[idx] = {
        ...sw,
        calculated_questions: ideal,
        percentage: state.totalQuestions > 0 ? Math.round((ideal / state.totalQuestions) * 100) : 0,
        marks: ideal * marksPerQ,
        chapters: updatedChaps
      }
    })

    updateState({ subjectWeightages: updatedSubjects })
  }

  // Sync a section's target to its available pool count when pool is insufficient
  const handleSyncSectionTargetToPool = (sec: any, availCount: number) => {
    const marksPerQ = state.markingScheme.positive_marks || 1
    const updatedSections = (state.blueprintSections || []).map((bs: any) => {
      if (bs.id === sec.id || bs.name === sec.name) {
        return { ...bs, total_questions: availCount }
      }
      return bs
    })

    const updatedSubjects = [...state.subjectWeightages]
    const sectionSubjectIndices = updatedSubjects
      .map((sw, idx) => ({ sw, idx }))
      .filter(({ sw }) => isSubjectMatchingSection(sw.subject_id, sw.name, sec))

    sectionSubjectIndices.forEach(({ sw, idx }) => {
      const ideal = Math.min(sw.available, availCount)
      updatedSubjects[idx] = {
        ...sw,
        calculated_questions: ideal,
        percentage: state.totalQuestions > 0 ? Math.round((ideal / state.totalQuestions) * 100) : 0,
        marks: ideal * marksPerQ,
        chapters: distributeQuestionsToChapters(sw.chapters, ideal)
      }
    })

    const newTotalQ = updatedSubjects.reduce((sum, sw) => sum + (sw.calculated_questions || 0), 0)

    updateState({
      blueprintSections: updatedSections,
      subjectWeightages: updatedSubjects,
      totalQuestions: newTotalQ,
      totalMarks: newTotalQ * marksPerQ
    })
  }

  // Reconcile unallocated or excess questions
  const handleAutoBalanceRemaining = () => {
    const marksPerQ = state.markingScheme.positive_marks || 1
    const currentSum = state.subjectWeightages.reduce((sum, sw) => sum + (sw.calculated_questions || 0), 0)
    const diff = state.totalQuestions - currentSum
    if (diff === 0) return

    let remainingDiff = diff
    const updatedSubjects = [...state.subjectWeightages]

    if (diff > 0) {
      for (let i = 0; i < updatedSubjects.length; i++) {
        const sw = updatedSubjects[i]
        if (sw.calculated_questions < sw.available) {
          const add = Math.min(remainingDiff, sw.available - sw.calculated_questions)
          const newQ = sw.calculated_questions + add
          updatedSubjects[i] = {
            ...sw,
            calculated_questions: newQ,
            percentage: state.totalQuestions > 0 ? Math.round((newQ / state.totalQuestions) * 100) : 0,
            marks: newQ * marksPerQ,
            chapters: distributeQuestionsToChapters(sw.chapters, newQ)
          }
          remainingDiff -= add
          if (remainingDiff === 0) break
        }
      }
    } else {
      let excess = Math.abs(diff)
      for (let i = updatedSubjects.length - 1; i >= 0; i--) {
        const sw = updatedSubjects[i]
        if (sw.calculated_questions > 0) {
          const sub = Math.min(excess, sw.calculated_questions)
          const newQ = sw.calculated_questions - sub
          updatedSubjects[i] = {
            ...sw,
            calculated_questions: newQ,
            percentage: state.totalQuestions > 0 ? Math.round((newQ / state.totalQuestions) * 100) : 0,
            marks: newQ * marksPerQ,
            chapters: distributeQuestionsToChapters(sw.chapters, newQ)
          }
          excess -= sub
          if (excess === 0) break
        }
      }
    }

    updateState({ subjectWeightages: updatedSubjects })
  }

  // Set totalQuestions to match current allocated questions sum
  const handleSyncTotalToAllocated = () => {
    const currentSum = state.subjectWeightages.reduce((sum, sw) => sum + (sw.calculated_questions || 0), 0)
    if (currentSum <= 0) return
    const marksPerQ = state.markingScheme.positive_marks || 1
    const updatedSubjects = state.subjectWeightages.map(sw => ({
      ...sw,
      percentage: Math.round((sw.calculated_questions / currentSum) * 100),
      marks: sw.calculated_questions * marksPerQ
    }))
    updateState({
      totalQuestions: currentSum,
      totalMarks: currentSum * marksPerQ,
      subjectWeightages: updatedSubjects
    })
  }

  const handleEqualizeSubjects = () => {
    const numSub = state.subjectWeightages.length
    if (numSub === 0) return
    const equalPct = Math.floor(100 / numSub)
    const remainder = 100 - equalPct * numSub

    const updated = state.subjectWeightages.map((sw, idx) => ({
      ...sw,
      percentage: equalPct + (idx === 0 ? remainder : 0)
    }))
    const recalculated = recalculateWeightages(updated, state.totalQuestions, state.totalMarks, 'percentage')
    updateState({ subjectWeightages: recalculated, weightageMode: 'percentage' })
  }

  const handleEqualizeChapters = (subjectId: number) => {
    const updated = state.subjectWeightages.map(sw => {
      if (sw.subject_id !== subjectId) return sw
      const numChaps = sw.chapters.length
      if (numChaps === 0) return sw
      const equalPct = Math.floor(100 / numChaps)
      const remainder = 100 - equalPct * numChaps

      const updatedChaps = sw.chapters.map((ch, idx) => {
        const pct = equalPct + (idx === 0 ? remainder : 0)
        return {
          ...ch,
          percentage: pct,
          calculated_questions: Math.round((pct / 100) * sw.calculated_questions)
        }
      })
      return { ...sw, chapters: updatedChaps }
    })
    updateState({ subjectWeightages: updated })
  }

  const toggleSubjectExpand = (subId: number) => {
    setExpandedSubjects(prev => ({ ...prev, [subId]: !prev[subId] }))
  }

  // Validation calculations
  const totalSubjectPct = state.subjectWeightages.reduce((sum, sw) => sum + (sw.percentage || 0), 0)
  const totalSubjectMarks = state.subjectWeightages.reduce((sum, sw) => sum + (sw.marks || 0), 0)
  const calculatedQuestionsSum = state.subjectWeightages.reduce((sum, sw) => sum + sw.calculated_questions, 0)
  const totalPoolAvailable = state.subjectWeightages.reduce((sum, sw) => sum + sw.available, 0)

  const pctValid = Math.abs(totalSubjectPct - 100) < 0.1
  const marksValid = Math.abs(totalSubjectMarks - state.totalMarks) < 0.5
  const questionsValid = calculatedQuestionsSum === state.totalQuestions

  const isValidSum = state.weightageMode === 'questions'
    ? questionsValid
    : state.weightageMode === 'percentage'
      ? pctValid
      : marksValid

  const totalDifficultyQuestions = state.difficultyMix.Easy + state.difficultyMix.Medium + state.difficultyMix.Hard
  const difficultyValid = totalDifficultyQuestions === 0 || totalDifficultyQuestions === state.totalQuestions
  const activeBlueprintSections = (state.blueprintSections || [])
    .filter((bs: any) => bs._selected !== false)
    .filter((sec: any) => {
      if (state.subjectWeightages.length === 0) return true
      return state.subjectWeightages.some(sw =>
        isSubjectMatchingSection(sw.subject_id, sw.name, sec) && (sw.available || 0) > 0
      )
    })
  const isTargetExceedingPool = totalPoolAvailable > 0 && state.totalQuestions > totalPoolAvailable
  const insufficientSubjects = state.subjectWeightages.filter(sw => sw.calculated_questions > sw.available)

  return (
    <div className="space-y-6">
      {/* 1. Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
            <BarChart3 className="h-5 w-5 text-primary" />
            Test Weightage & Question Distribution
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure target question totals and subject/chapter weightage breakdown
          </p>
        </div>
      </div>

      {/* 2. Top Totals Overview Bar (Combined card on mobile, 2-column on desktop) */}
      <div className={`bg-card border ${isTargetExceedingPool ? 'border-destructive ring-1 ring-destructive/30' : 'border-border/80'} rounded-2xl shadow-glass-xs relative overflow-hidden sm:border-0 sm:shadow-none sm:bg-transparent sm:grid sm:grid-cols-2 sm:gap-4`}>
        {/* Card / Row 1: Total Questions Target */}
        <div className={`p-3 sm:p-4 sm:rounded-2xl sm:bg-card sm:border ${isTargetExceedingPool ? 'sm:border-destructive sm:ring-1 sm:ring-destructive/30' : 'sm:border-border/80'} sm:shadow-glass-xs`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <Label htmlFor="total-qs" className={`text-xs font-bold uppercase tracking-wider block truncate ${isTargetExceedingPool ? 'text-destructive' : 'text-muted-foreground'}`}>
                <span className="sm:hidden">Questions</span>
                <span className="hidden sm:inline">Total Questions Target</span>
              </Label>
              <p className="text-[11px] text-muted-foreground font-medium truncate mt-0.5 hidden sm:block">
                Available in pool: <strong className={isTargetExceedingPool ? 'text-destructive font-bold' : 'text-foreground font-bold'}>{totalPoolAvailable} Qs</strong>
              </p>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <Input
                id="total-qs"
                type="number"
                min="1"
                max={totalPoolAvailable > 0 ? totalPoolAvailable : undefined}
                value={state.totalQuestions === 0 ? '' : state.totalQuestions}
                onChange={e => handleTotalQuestionsChange(e.target.value)}
                className={`h-8 sm:h-9 w-16 sm:w-24 text-center font-extrabold text-sm bg-background rounded-xl ${
                  isTargetExceedingPool
                    ? 'border-destructive text-destructive focus:ring-2 focus:ring-destructive/40'
                    : 'border-primary/50 focus:ring-2 focus:ring-primary/40'
                }`}
              />
              <span className="text-xs font-semibold text-muted-foreground">
                <span className="sm:hidden">/ {totalPoolAvailable} Qs</span>
                <span className="hidden sm:inline font-bold text-foreground">Qs</span>
              </span>
            </div>
          </div>

          {isTargetExceedingPool && (
            <p className="text-[10px] font-bold text-destructive flex items-center gap-1 mt-1.5 pt-1.5 border-t border-destructive/20">
              <AlertTriangle className="h-3 w-3 shrink-0" /> Cannot exceed {totalPoolAvailable} Qs in pool
            </p>
          )}
        </div>

        {/* Card / Row 2: Total Test Marks */}
        <div className="p-3 sm:p-4 border-t border-border/60 sm:border-t-0 sm:rounded-2xl sm:bg-card sm:border sm:border-border/80 sm:shadow-glass-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block truncate">
                <span className="sm:hidden">Total Marks</span>
                <span className="hidden sm:inline">Total Test Marks</span>
              </span>
              <p className="text-[11px] text-muted-foreground font-medium truncate mt-0.5 hidden sm:block">
                ({state.totalQuestions} Qs × +{state.markingScheme.positive_marks ?? 1} mark/Q{state.markingScheme.negative_marking_enabled && state.markingScheme.negative_marks > 0 ? `, -${state.markingScheme.negative_marks} neg` : ''})
              </p>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              {state.markingScheme.negative_marking_enabled && state.markingScheme.negative_marks > 0 && (
                <span className="text-[11px] font-bold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-md">
                  -{state.markingScheme.negative_marks}
                </span>
              )}
              <div className="text-base sm:text-xl font-black text-primary flex items-baseline gap-1">
                {state.totalMarks} <span className="text-xs font-semibold text-muted-foreground">Marks</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Question Allocation Reconciliation Banner */}
      {!questionsValid && (
        <div className={`p-3 sm:p-3.5 rounded-2xl border flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 text-xs shadow-glass-xs ${
          calculatedQuestionsSum < state.totalQuestions
            ? 'bg-secondary/10 border-secondary/30 text-secondary'
            : 'bg-destructive/10 border-destructive/30 text-destructive'
        }`}>
          <div className="flex items-start sm:items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 sm:mt-0" />
            <span className="text-foreground leading-snug">
              <strong>Allocated: {calculatedQuestionsSum} / {state.totalQuestions} Qs</strong>
              {' '}({calculatedQuestionsSum < state.totalQuestions
                ? `${state.totalQuestions - calculatedQuestionsSum} Qs unallocated`
                : `+${calculatedQuestionsSum - state.totalQuestions} Qs over target`})
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAutoBalanceRemaining}
              className="h-8 text-xs font-bold border-border bg-card text-foreground hover:bg-muted rounded-xl flex-1 sm:flex-initial"
            >
              {calculatedQuestionsSum < state.totalQuestions ? 'Auto-Balance Remaining' : 'Trim Excess'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSyncTotalToAllocated}
              className="h-8 text-xs font-bold border-border bg-card text-foreground hover:bg-muted rounded-xl flex-1 sm:flex-initial"
            >
              Set Total to {calculatedQuestionsSum} Qs
            </Button>
          </div>
        </div>
      )}

      {/* 4. Hierarchical Weightage Distribution (Section -> Subject -> Chapter Tree) */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 bg-muted/30 p-3 sm:p-3.5 rounded-2xl border border-border/70">
          <div className="flex items-center gap-2 flex-wrap justify-between sm:justify-start">
            <Label className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-1.5">
              <BarChart3 className="h-4 w-4 text-primary" />
              {activeBlueprintSections.length > 0 ? 'Hierarchical Distribution' : 'Subject & Chapter Distribution'}
            </Label>
            <Badge variant="outline" className="text-[10px] font-mono bg-card px-2 py-0.5 rounded-md">
              {activeBlueprintSections.length > 0 ? `${activeBlueprintSections.length} Sections` : `${state.subjectWeightages.length} Subjects`}
            </Badge>
          </div>

          {/* Auto-Balance Tools (Single row on mobile) */}
          <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
            {/* 🎯 Fill by Blueprint Targets (Strict Section Targets) */}
            {activeBlueprintSections.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleFillStrictlySectionTargets}
                className="text-xs h-7 sm:h-8 px-2 sm:px-3 gap-1 sm:gap-1.5 font-bold border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 rounded-xl transition-all shadow-2xs flex-1 sm:flex-initial shrink-0"
                title="Fill question allocations strictly per section blueprint targets (up to available pool)"
              >
                <Layers className="h-3.5 w-3.5" />
                <span className="sm:hidden">Blueprint</span>
                <span className="hidden sm:inline">Fill by Blueprint</span>
              </Button>
            )}

            {/* ⚡ Intelligent Auto-Balance from Pool */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoBalanceFromPool}
              className={`text-xs h-7 sm:h-8 px-2 sm:px-3 gap-1 sm:gap-1.5 font-bold rounded-xl transition-all shadow-2xs flex-1 sm:flex-initial shrink-0 ${
                activeBlueprintSections.length > 0
                  ? 'border-border/80 bg-card text-foreground hover:bg-muted'
                  : 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'
              }`}
              title="Intelligently balance questions across all subjects based on pool size to meet total questions target"
            >
              <Zap className="h-3.5 w-3.5" />
              <span className="sm:hidden">Auto-Balance</span>
              <span className="hidden sm:inline">Auto-Balance from Pool</span>
            </Button>

            {/* Equalize Subjects */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleEqualizeSubjects}
              className="text-xs h-7 sm:h-8 px-2 sm:px-2.5 gap-1 rounded-xl bg-card border-border/80 text-foreground hover:bg-muted font-medium shrink-0"
              title="Distribute questions equally across all subjects"
            >
              <Equal className="h-3 w-3" /> Equalize
            </Button>
          </div>
        </div>

        {/* Tree Container */}
        {activeBlueprintSections.length > 0 ? (
          /* ─── 3-LEVEL TREE: SECTION -> SUBJECT -> CHAPTER ─── */
          <div className="space-y-4">
            {activeBlueprintSections.map((sec: any, secIdx: number) => {
              // Group matching subjects for this section
              const sectionSubjects = state.subjectWeightages.filter(sw =>
                isSubjectMatchingSection(sw.subject_id, sw.name, sec)
              )

              const subjectsToRender = sectionSubjects.length > 0 ? sectionSubjects : []

              const allocatedSectionQ = subjectsToRender.reduce((sum, sw) => sum + (sw.calculated_questions || 0), 0)
              const targetSectionQ = Number(sec.total_questions) || 0
              const totalAvailInSection = subjectsToRender.reduce((sum, sw) => sum + (sw.available || 0), 0)
              const isFulfilled = allocatedSectionQ === targetSectionQ
              const isOver = allocatedSectionQ > targetSectionQ
              const isUnder = allocatedSectionQ < targetSectionQ
              const hasPoolDeficit = totalAvailInSection < targetSectionQ

              return (
                <div key={sec.id || secIdx} className="bg-card border border-border/80 rounded-2xl overflow-hidden shadow-glass-xs transition-all">
                  {/* Level 1: Section Header Banner */}
                  <div className="p-3 sm:p-4 bg-muted/40 border-b border-border/60 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-2.5">
                      <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0 mt-0.5 sm:mt-0">
                        {String.fromCharCode(65 + secIdx)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h3 className="text-xs sm:text-sm font-bold text-foreground break-words">{sec.name}</h3>
                          <Badge variant="outline" className="text-[10px] font-bold bg-background text-foreground border-border">
                            Target: {targetSectionQ} Qs
                          </Badge>
                          {sec.duration_minutes && (
                            <Badge variant="outline" className="text-[10px] font-mono text-muted-foreground border-border">
                              {sec.duration_minutes} mins
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 break-words">
                          Assigned Marking: <strong className="text-primary font-medium">{sec.marking_rule_name || 'Standard Scheme'}</strong>
                        </p>
                      </div>
                    </div>

                    {/* Section Fulfillment Indicator */}
                    <div className="flex items-center justify-between md:justify-end gap-2 self-stretch md:self-auto flex-wrap pt-2 md:pt-0 border-t md:border-t-0 border-border/40">
                      <div className="text-left md:text-right">
                        <div className="text-xs font-bold text-foreground">
                          {allocatedSectionQ} / {targetSectionQ} Qs
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {targetSectionQ > 0 ? `${Math.round((allocatedSectionQ / targetSectionQ) * 100)}% Fulfilled` : '0%'}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge
                          variant="outline"
                          className={`text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-lg ${
                            isFulfilled
                              ? 'bg-secondary/15 text-secondary border-secondary/30'
                              : isOver
                              ? 'bg-destructive/15 text-destructive border-destructive/30'
                              : 'bg-primary/10 text-primary border-primary/30'
                          }`}
                        >
                          {isFulfilled
                            ? 'Target Met'
                            : isOver
                            ? `+${allocatedSectionQ - targetSectionQ} Over`
                            : hasPoolDeficit
                            ? `${targetSectionQ - totalAvailInSection} Deficit`
                            : `${targetSectionQ - allocatedSectionQ} Needed`}
                        </Badge>

                        {hasPoolDeficit && targetSectionQ > totalAvailInSection && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncSectionTargetToPool(sec, totalAvailInSection)}
                            className="h-7 px-2 text-[10px] sm:text-[11px] font-bold text-secondary border-secondary/30 bg-secondary/5 hover:bg-secondary/15 rounded-lg flex items-center gap-1"
                            title="Set section target to match available pool"
                          >
                            Sync ({totalAvailInSection})
                          </Button>
                        )}

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAutoBalanceSection(sec)}
                          className="h-7 px-2 text-[10px] sm:text-[11px] font-bold text-primary border-primary/30 bg-primary/5 hover:bg-primary/15 rounded-lg flex items-center gap-1"
                          title="Auto-populate this section's target from available pool"
                        >
                          <Zap className="h-3 w-3" /> Auto-Balance
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Level 2 & 3: Mobile Cards (md:hidden) */}
                  <div className="md:hidden p-3 space-y-2.5">
                    {subjectsToRender.map(sw => {
                      const isExpanded = !!expandedSubjects[sw.subject_id]
                      const isInsufficient = sw.calculated_questions > sw.available

                      return (
                        <div
                          key={`sec-mobile-${sw.subject_id}`}
                          className={`p-3 rounded-xl border space-y-2.5 transition-all ${
                            isInsufficient ? 'bg-destructive/5 border-destructive/30' : 'bg-card border-border/80 shadow-2xs'
                          }`}
                        >
                          {/* Row 1: Expand Button + Subject Name + Badge */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                              {sw.chapters.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => toggleSubjectExpand(sw.subject_id)}
                                  className="text-muted-foreground hover:text-foreground p-0.5 rounded-md hover:bg-muted transition-colors shrink-0 mt-0.5"
                                >
                                  {isExpanded ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                              ) : (
                                <span className="w-4 shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <span className="font-bold text-xs sm:text-sm text-foreground break-words block leading-snug">
                                  {sw.name}
                                </span>
                                {sw.chapters.length > 0 && (
                                  <span className="text-[10px] text-muted-foreground font-medium">
                                    {sw.chapters.length} {sw.chapters.length === 1 ? 'chapter' : 'chapters'}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="shrink-0">
                              {isInsufficient ? (
                                <Badge variant="destructive" className="text-[9px] gap-1 py-0 px-1.5 rounded-md font-bold">
                                  <AlertCircle className="h-2.5 w-2.5" /> Deficit
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[9px] bg-secondary/10 text-secondary border-secondary/30 gap-1 py-0 px-1.5 rounded-md font-bold">
                                  <CheckCircle2 className="h-2.5 w-2.5" /> Ready
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Row 2: Controls bar */}
                          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/40 text-xs">
                            <div className="flex items-center gap-1.5">
                              <Input
                                type="number"
                                value={sw.calculated_questions === 0 ? '' : sw.calculated_questions}
                                onChange={e => handleSubjectQuestionsChange(sw.subject_id, e.target.value)}
                                className="h-8 w-20 text-center text-xs font-bold border-primary/50 bg-background rounded-lg focus:ring-primary"
                              />
                              <span className="text-muted-foreground text-[11px] font-medium">Qs</span>
                            </div>

                            <div className="text-[11px] font-semibold text-foreground">
                              {sw.calculated_questions * (state.markingScheme.positive_marks || 1)} pts
                            </div>

                            <div className={`text-[11px] font-mono ${isInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                              Pool: {sw.available} Qs
                            </div>
                          </div>

                          {/* Expanded Chapters (mobile) */}
                          {isExpanded && sw.chapters.length > 0 && (
                            <div className="pt-2 border-t border-border/60 space-y-2">
                              <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                                <span className="flex items-center gap-1">
                                  <Layers className="h-3 w-3 text-primary" /> Chapters
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleEqualizeChapters(sw.subject_id)}
                                  className="text-[10px] text-primary hover:underline font-bold flex items-center gap-1"
                                >
                                  <Equal className="h-2.5 w-2.5" /> Equalize
                                </button>
                              </div>

                              <div className="space-y-1.5">
                                {sw.chapters.map(ch => {
                                  const isChInsufficient = ch.calculated_questions > ch.available
                                  return (
                                    <div key={ch.chapter_id} className="p-2.5 bg-muted/30 rounded-xl border border-border/50 space-y-1.5 text-xs">
                                      <div className="font-medium text-foreground break-words text-[11px] leading-snug">
                                        {ch.name}
                                      </div>
                                      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/30">
                                        <div className="flex items-center gap-1">
                                          <Input
                                            type="number"
                                            value={ch.calculated_questions === 0 ? '' : ch.calculated_questions}
                                            onChange={e => handleChapterQuestionsChange(sw.subject_id, ch.chapter_id, e.target.value)}
                                            className="h-6 w-16 text-center text-xs p-0.5 rounded font-bold bg-background"
                                          />
                                          <span className="text-[10px] text-muted-foreground">Qs</span>
                                        </div>
                                        <span className={`text-[10px] font-mono ${isChInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                                          Pool: {ch.available} Qs
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Level 2 & 3: Subjects and Chapters Table (Desktop: hidden md:table) */}
                  <table className="w-full text-xs text-left hidden md:table">
                    <thead className="bg-muted/20 border-b border-border/60 text-muted-foreground uppercase text-[10px] font-bold tracking-wider">
                      <tr>
                        <th className="py-2.5 px-4">Subject / Chapter Node</th>
                        <th className="py-2.5 px-3 text-center text-primary font-black bg-primary/5">
                          Questions (#)
                        </th>
                        <th className="py-2.5 px-3 text-center">
                          Marks
                        </th>
                        <th className="py-2.5 px-3 text-center">Pool Available</th>
                        <th className="py-2.5 px-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y border-border/60">
                      {subjectsToRender.map(sw => {
                        const isExpanded = !!expandedSubjects[sw.subject_id]
                        const isInsufficient = sw.calculated_questions > sw.available

                        return (
                          <React.Fragment key={sw.subject_id}>
                            {/* Subject Row (Level 2) */}
                            <tr
                              className={`hover:bg-muted/30 transition-colors group cursor-pointer ${
                                isInsufficient ? 'bg-destructive/5' : ''
                              }`}
                              onClick={(e) => {
                                const target = e.target as HTMLElement
                                if (!target.closest('input') && !target.closest('button')) {
                                  toggleSubjectExpand(sw.subject_id)
                                }
                              }}
                            >
                              <td className="py-3 px-4 font-medium text-foreground">
                                <div className="flex items-center gap-2.5">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      toggleSubjectExpand(sw.subject_id)
                                    }}
                                    className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-muted/80 transition-colors shrink-0"
                                  >
                                    {isExpanded ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4" />}
                                  </button>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-foreground text-xs">{sw.name}</span>
                                    {sw.chapters.length > 0 && (
                                      <Badge variant="outline" className="text-[10px] font-medium py-0 px-2 rounded-full bg-muted/30 border-border/70 text-muted-foreground">
                                        {sw.chapters.length} {sw.chapters.length === 1 ? 'chapter' : 'chapters'}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Questions Input */}
                              <td className="py-2.5 px-3 text-center bg-primary/5">
                                <div className="inline-flex items-center gap-1 justify-center">
                                  <Input
                                    type="number"
                                    value={sw.calculated_questions === 0 ? '' : sw.calculated_questions}
                                    onChange={e => handleSubjectQuestionsChange(sw.subject_id, e.target.value)}
                                    className="h-7 w-20 text-center text-xs font-bold border-primary/50 bg-card rounded-lg focus:ring-primary"
                                  />
                                  <span className="text-muted-foreground text-[11px] font-medium">Qs</span>
                                </div>
                              </td>

                              {/* Marks Display */}
                              <td className="py-2.5 px-3 text-center font-semibold text-foreground">
                                {sw.calculated_questions * (state.markingScheme.positive_marks || 1)} pts
                              </td>

                              {/* Pool Available */}
                              <td className="py-2.5 px-3 text-center font-medium">
                                <span className={`font-mono text-xs ${isInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                                  {sw.available} Qs
                                </span>
                              </td>

                              {/* Status */}
                              <td className="py-2.5 px-3 text-center">
                                {isInsufficient ? (
                                  <Badge variant="destructive" className="text-[10px] gap-1 py-0 px-2 rounded-md font-bold">
                                    <AlertCircle className="h-3 w-3" /> Deficit
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] bg-secondary/10 text-secondary border-secondary/30 gap-1 py-0 px-2 rounded-md font-bold">
                                    <CheckCircle2 className="h-3 w-3" /> Ready
                                  </Badge>
                                )}
                              </td>
                            </tr>

                            {/* Chapter Accordion Rows (Level 3) */}
                            {isExpanded && sw.chapters.length > 0 && (
                              <tr className="bg-muted/10">
                                <td colSpan={5} className="p-0">
                                  <div className="py-3 px-6 border-y border-border/50 bg-muted/20 space-y-2">
                                    <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-4">
                                      <span className="flex items-center gap-1.5">
                                        <Layers className="h-3 w-3 text-primary" /> Chapter Breakdown ({sw.name})
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleEqualizeChapters(sw.subject_id)}
                                        className="text-[10px] text-primary hover:underline font-bold flex items-center gap-1"
                                      >
                                        <Equal className="h-2.5 w-2.5" /> Equalize Chapters
                                      </button>
                                    </div>
                                    <div className="space-y-1.5 pl-4">
                                      {sw.chapters.map(ch => {
                                        const isChInsufficient = ch.calculated_questions > ch.available
                                        return (
                                          <div key={ch.chapter_id} className="flex items-center justify-between text-xs py-1.5 px-3.5 bg-card rounded-xl border border-border/60">
                                            <span className="font-medium text-foreground truncate max-w-[240px]">{ch.name}</span>
                                            <div className="flex items-center gap-4">
                                              <div className="flex items-center gap-1">
                                                <span className="text-[11px] text-muted-foreground font-medium">Questions:</span>
                                                <Input
                                                  type="number"
                                                  value={ch.calculated_questions === 0 ? '' : ch.calculated_questions}
                                                  onChange={e => handleChapterQuestionsChange(sw.subject_id, ch.chapter_id, e.target.value)}
                                                  className="h-6 w-14 text-center text-xs p-0.5 rounded font-bold"
                                                />
                                                <span className="text-[11px] text-muted-foreground">Qs</span>
                                              </div>
                                              <span className="text-[11px] font-semibold text-foreground">
                                                Target: <strong>{ch.calculated_questions} Qs</strong>
                                              </span>
                                              <span className={`text-[11px] font-mono ${isChInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                                                Pool: {ch.available} Qs
                                              </span>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            })}
          </div>
        ) : (
          /* ─── 2-LEVEL FLAT TREE: SUBJECT -> CHAPTER (WHEN NO SECTIONS) ─── */
          <div className="border border-border/80 rounded-2xl overflow-hidden bg-card shadow-glass-xs">
            {/* Mobile Cards (md:hidden) */}
            <div className="md:hidden p-3 space-y-2.5">
              {state.subjectWeightages.map(sw => {
                const isExpanded = !!expandedSubjects[sw.subject_id]
                const isInsufficient = sw.calculated_questions > sw.available

                return (
                  <div
                    key={`flat-mobile-${sw.subject_id}`}
                    className={`p-3 rounded-xl border space-y-2.5 transition-all ${
                      isInsufficient ? 'bg-destructive/5 border-destructive/30' : 'bg-card border-border/80 shadow-2xs'
                    }`}
                  >
                    {/* Row 1: Expand Button + Subject Name + Badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {sw.chapters.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleSubjectExpand(sw.subject_id)}
                            className="text-muted-foreground hover:text-foreground p-0.5 rounded-md hover:bg-muted transition-colors shrink-0 mt-0.5"
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        ) : (
                          <span className="w-4 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="font-bold text-xs sm:text-sm text-foreground break-words block leading-snug">
                            {sw.name}
                          </span>
                          {sw.chapters.length > 0 && (
                            <span className="text-[10px] text-muted-foreground font-medium">
                              {sw.chapters.length} {sw.chapters.length === 1 ? 'chapter' : 'chapters'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="shrink-0">
                        {isInsufficient ? (
                          <Badge variant="destructive" className="text-[9px] gap-1 py-0 px-1.5 rounded-md font-bold">
                            <AlertCircle className="h-2.5 w-2.5" /> Deficit
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[9px] bg-secondary/10 text-secondary border-secondary/30 gap-1 py-0 px-1.5 rounded-md font-bold">
                            <CheckCircle2 className="h-2.5 w-2.5" /> Ready
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Controls bar */}
                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/40 text-xs">
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          value={sw.calculated_questions === 0 ? '' : sw.calculated_questions}
                          onChange={e => handleSubjectQuestionsChange(sw.subject_id, e.target.value)}
                          className="h-8 w-20 text-center text-xs font-bold border-primary/50 bg-background rounded-lg focus:ring-primary"
                        />
                        <span className="text-muted-foreground text-[11px] font-medium">Qs</span>
                      </div>

                      <div className="text-[11px] font-semibold text-foreground">
                        {sw.calculated_questions * (state.markingScheme.positive_marks || 1)} pts
                      </div>

                      <div className={`text-[11px] font-mono ${isInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                        Pool: {sw.available} Qs
                      </div>
                    </div>

                    {/* Expanded Chapters (mobile) */}
                    {isExpanded && sw.chapters.length > 0 && (
                      <div className="pt-2 border-t border-border/60 space-y-2">
                        <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                          <span className="flex items-center gap-1">
                            <Layers className="h-3 w-3 text-primary" /> Chapters
                          </span>
                          <button
                            type="button"
                            onClick={() => handleEqualizeChapters(sw.subject_id)}
                            className="text-[10px] text-primary hover:underline font-bold flex items-center gap-1"
                          >
                            <Equal className="h-2.5 w-2.5" /> Equalize
                          </button>
                        </div>

                        <div className="space-y-1.5">
                          {sw.chapters.map(ch => {
                            const isChInsufficient = ch.calculated_questions > ch.available
                            return (
                              <div key={ch.chapter_id} className="p-2.5 bg-muted/30 rounded-xl border border-border/50 space-y-1.5 text-xs">
                                <div className="font-medium text-foreground break-words text-[11px] leading-snug">
                                  {ch.name}
                                </div>
                                <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/30">
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      value={ch.calculated_questions === 0 ? '' : ch.calculated_questions}
                                      onChange={e => handleChapterQuestionsChange(sw.subject_id, ch.chapter_id, e.target.value)}
                                      className="h-6 w-16 text-center text-xs p-0.5 rounded font-bold bg-background"
                                    />
                                    <span className="text-[10px] text-muted-foreground">Qs</span>
                                  </div>
                                  <span className={`text-[10px] font-mono ${isChInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                                    Pool: {ch.available} Qs
                                  </span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Mobile Total Summary Card */}
              <div className="p-3 bg-muted/40 rounded-xl border border-border/80 space-y-1.5 text-xs">
                <div className="flex items-center justify-between font-bold">
                  <span className="uppercase text-[10px] tracking-wider text-muted-foreground">Total Allocation</span>
                  <span className={questionsValid ? 'text-secondary font-black' : 'text-destructive font-black'}>
                    {calculatedQuestionsSum} / {state.totalQuestions} Qs
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Total Marks:</span>
                  <span className="font-bold text-foreground">
                    {calculatedQuestionsSum * (state.markingScheme.positive_marks || 1)} / {state.totalMarks} pts
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Pool Available:</span>
                  <span className="font-mono">{totalPoolAvailable} Qs</span>
                </div>
              </div>
            </div>

            {/* Desktop Table (hidden md:table) */}
            <table className="w-full text-xs text-left hidden md:table">
              <thead className="bg-muted/40 border-b border-border/60 text-muted-foreground uppercase text-[10px] font-bold tracking-wider">
                <tr>
                  <th className="py-2.5 px-4">Subject / Chapter</th>
                  <th className="py-2.5 px-3 text-center text-primary font-black bg-primary/5">
                    Questions (#)
                  </th>
                  <th className="py-2.5 px-3 text-center">
                    Marks
                  </th>
                  <th className="py-2.5 px-3 text-center">Pool Available</th>
                  <th className="py-2.5 px-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y border-border/60">
                {state.subjectWeightages.map(sw => {
                  const isExpanded = !!expandedSubjects[sw.subject_id]
                  const isInsufficient = sw.calculated_questions > sw.available

                  return (
                    <React.Fragment key={sw.subject_id}>
                      {/* Subject Row */}
                      <tr className={`hover:bg-muted/20 transition-colors ${isInsufficient ? 'bg-destructive/5' : ''}`}>
                        <td className="py-3 px-4 font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            {sw.chapters.length > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleSubjectExpand(sw.subject_id)}
                                className="text-muted-foreground hover:text-foreground p-0.5 rounded-md hover:bg-muted transition-colors"
                              >
                                {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-primary" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            <span className="font-bold text-foreground">{sw.name}</span>
                            {sw.chapters.length > 0 && (
                              <Badge variant="outline" className="text-[10px] font-normal py-0 px-1.5 rounded-md bg-muted/40">
                                {sw.chapters.length} chapters
                              </Badge>
                            )}
                          </div>
                        </td>

                        {/* Questions Input */}
                        <td className="py-2.5 px-3 text-center bg-primary/5">
                          <div className="inline-flex items-center gap-1 justify-center">
                            <Input
                              type="number"
                              value={sw.calculated_questions === 0 ? '' : sw.calculated_questions}
                              onChange={e => handleSubjectQuestionsChange(sw.subject_id, e.target.value)}
                              className="h-7 w-20 text-center text-xs font-bold border-primary/50 bg-card rounded-lg focus:ring-primary"
                            />
                            <span className="text-muted-foreground text-[11px] font-medium">Qs</span>
                          </div>
                        </td>

                        {/* Marks Display */}
                        <td className="py-2.5 px-3 text-center font-semibold text-foreground">
                          {sw.calculated_questions * (state.markingScheme.positive_marks || 1)} pts
                        </td>

                        {/* Pool Available */}
                        <td className="py-2.5 px-3 text-center text-muted-foreground font-mono">
                          {sw.available} Qs
                        </td>

                        {/* Status */}
                        <td className="py-2.5 px-3 text-center">
                          {isInsufficient ? (
                            <Badge variant="destructive" className="text-[10px] gap-1 py-0 px-2 rounded-md font-bold">
                              <AlertCircle className="h-3 w-3" /> Deficit
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-secondary/10 text-secondary border-secondary/30 gap-1 py-0 px-2 rounded-md font-bold">
                              <CheckCircle2 className="h-3 w-3" /> Ready
                            </Badge>
                          )}
                        </td>
                      </tr>

                      {/* Chapter Accordion Rows */}
                      {isExpanded && sw.chapters.length > 0 && (
                        <tr className="bg-muted/10">
                          <td colSpan={5} className="p-0">
                            <div className="py-3 px-6 border-y border-border/50 bg-muted/20 space-y-2">
                              <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                                <span className="flex items-center gap-1.5">
                                  <Layers className="h-3 w-3 text-primary" /> Chapter Breakdown ({sw.name})
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleEqualizeChapters(sw.subject_id)}
                                  className="text-[10px] text-primary hover:underline font-bold flex items-center gap-1"
                                >
                                  <Equal className="h-2.5 w-2.5" /> Equalize Chapters
                                </button>
                              </div>
                              <div className="space-y-1.5">
                                {sw.chapters.map(ch => {
                                  const isChInsufficient = ch.calculated_questions > ch.available
                                  return (
                                    <div key={ch.chapter_id} className="flex items-center justify-between text-xs py-1.5 px-3.5 bg-card rounded-xl border border-border/60">
                                      <span className="font-medium text-foreground truncate max-w-[240px]">{ch.name}</span>
                                      <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                          <span className="text-[11px] text-muted-foreground font-medium">Questions:</span>
                                          <Input
                                            type="number"
                                            value={ch.calculated_questions === 0 ? '' : ch.calculated_questions}
                                            onChange={e => handleChapterQuestionsChange(sw.subject_id, ch.chapter_id, e.target.value)}
                                            className="h-6 w-14 text-center text-xs p-0.5 rounded font-bold"
                                          />
                                          <span className="text-[11px] text-muted-foreground">Qs</span>
                                        </div>
                                        <span className="text-[11px] font-semibold text-foreground">
                                          Target: <strong>{ch.calculated_questions} Qs</strong>
                                        </span>
                                        <span className={`text-[11px] font-mono ${isChInsufficient ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                                          Pool: {ch.available} Qs
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}

                {/* Total Summary Row */}
                <tr className="bg-muted/40 font-bold border-t border-border/80">
                  <td className="py-3 px-4 uppercase text-[10px] tracking-wider text-muted-foreground">Total</td>
                  <td className="py-3 px-3 text-center bg-primary/10">
                    <span className={questionsValid ? 'text-secondary font-black' : 'text-destructive font-black'}>
                      {calculatedQuestionsSum} / {state.totalQuestions} Qs
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-secondary font-black">
                      {calculatedQuestionsSum * (state.markingScheme.positive_marks || 1)} / {state.totalMarks} pts
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center text-muted-foreground font-mono">
                    {totalPoolAvailable} Qs
                  </td>
                  <td className="py-3 px-3 text-center">
                    {isValidSum && insufficientSubjects.length === 0 ? (
                      <Badge variant="outline" className="text-secondary bg-secondary/10 border-secondary/30 text-[11px] font-bold">
                        Valid
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-[11px] font-bold">
                        {insufficientSubjects.length > 0 ? 'Deficit' : 'Mismatch'}
                      </Badge>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. Global Difficulty Mix Card */}
      <div className="bg-muted/20 border border-border/80 rounded-2xl p-3.5 sm:p-4 space-y-3 shadow-glass-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-1.5">
            <Sliders className="h-3.5 w-3.5 text-primary shrink-0" /> Global Difficulty Mix Targets
          </Label>
          <span className="text-[11px] text-muted-foreground">
            Optional target per difficulty
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="space-y-1">
            <Label htmlFor="diff-easy" className="text-[11px] sm:text-xs font-bold text-secondary">Easy Qs</Label>
            <Input
              id="diff-easy"
              type="number"
              value={state.difficultyMix.Easy === 0 ? '' : state.difficultyMix.Easy}
              onChange={e => updateState({
                difficultyMix: { ...state.difficultyMix, Easy: parseNum(e.target.value) }
              })}
              placeholder="0"
              className="h-8 text-xs font-bold rounded-xl bg-card border-border/70 text-center"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="diff-medium" className="text-[11px] sm:text-xs font-bold text-tertiary">Medium Qs</Label>
            <Input
              id="diff-medium"
              type="number"
              value={state.difficultyMix.Medium === 0 ? '' : state.difficultyMix.Medium}
              onChange={e => updateState({
                difficultyMix: { ...state.difficultyMix, Medium: parseNum(e.target.value) }
              })}
              placeholder="0"
              className="h-8 text-xs font-bold rounded-xl bg-card border-border/70 text-center"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="diff-hard" className="text-[11px] sm:text-xs font-bold text-destructive">Hard Qs</Label>
            <Input
              id="diff-hard"
              type="number"
              value={state.difficultyMix.Hard === 0 ? '' : state.difficultyMix.Hard}
              onChange={e => updateState({
                difficultyMix: { ...state.difficultyMix, Hard: parseNum(e.target.value) }
              })}
              placeholder="0"
              className="h-8 text-xs font-bold rounded-xl bg-card border-border/70 text-center"
            />
          </div>
        </div>

        {totalDifficultyQuestions > 0 && !difficultyValid && (
          <p className="text-[11px] text-destructive font-semibold flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Difficulty targets ({totalDifficultyQuestions}) do not match total test questions ({state.totalQuestions}).
          </p>
        )}
      </div>
    </div>
  )
}

